import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Check, ChevronDown, Lock, MessageCircle } from 'lucide-react'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import type { RegionId, SubscriptionPlan } from '@snootsvet/shared'
import type { IncompletePayment, PortalPlan, UpgradeOption } from '@snootsvet/shared/portal'
import {
  formatCurrency,
  HEALTHY_SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_PLAN_LABELS,
} from '@snootsvet/shared/portal'

import { PaymentProcessing } from '@/customer/components/billing/billing-primitives'
import { PaymentMethods } from '@/customer/components/billing/payment-methods'
import { PaymentReconciliationFailed } from '@/customer/components/billing/payment-reconciliation-failed'
import { PlanOptionCard } from '@/customer/components/billing/plan-option-card'
import { StripePaymentWidget } from '@/customer/components/billing/stripe-payment-widget'
import {
  describePlanAmount,
  describePlanPrice,
} from '@/customer/components/billing/subscription-plans'
import { ContactSupportAction } from '@/customer/components/contact-support-action'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { NotFound } from '@/customer/components/not-found'
import { PageContainer } from '@/customer/components/page-container'
import type { PetScopeContext } from '@/customer/components/pet-scope'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/customer/components/ui/alert'
import { Button } from '@/customer/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/customer/components/ui/collapsible'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import {
  petSubscriptionQueryKey,
  useGetPetSubscription,
} from '@/customer/hooks/use-get-pet-subscription'
import { useGetSubscriptionUpgrade } from '@/customer/hooks/use-get-subscription-upgrade'
import { invoicesQueryKey } from '@/customer/hooks/use-list-invoices'
import { useListPlans } from '@/customer/hooks/use-list-plans'
import { usePostSubscriptionUpgrade } from '@/customer/hooks/use-post-subscription-upgrade'
import { useRegion } from '@/customer/hooks/use-region'
import { useStripe } from '@/customer/hooks/use-stripe'
import { captureEvent } from '@/customer/lib/posthog'
import { routes } from '@/customer/routes'

const RECONCILIATION_POLL_MS = 2_000
const MAX_RECONCILIATION_ATTEMPTS = 5

/**
 * Subscription upgrade page. Shows available upgrade options with
 * Stripe-computed prorated amounts, and lets the member confirm
 * an immediate plan change.
 */
export function PetUpgradePage() {
  useDocumentTitle('Upgrade plan')
  const { pet, member } = useOutletContext<PetScopeContext>()
  const region = useRegion()
  const queryClient = useQueryClient()
  const stripe = useStripe(member.region)
  const preview = useGetSubscriptionUpgrade(pet.id)
  const plans = useListPlans()
  const upgrade = usePostSubscriptionUpgrade(pet.id)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null)
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null)
  const [attemptToken, setAttemptToken] = useState(() => crypto.randomUUID())
  const [incompletePayment, setIncompletePayment] = useState<IncompletePayment | null>(null)
  const [reconciliationAttempts, setReconciliationAttempts] = useState(0)
  const [initialCardError, setInitialCardError] = useState<string | null>(null)
  const [confirmingCard, setConfirmingCard] = useState(false)
  const [success, setSuccess] = useState(false)
  // Snapshotted at first confirm: the upgrade mutation invalidates the preview, so by the
  // time a 3DS payment settles `preview.data.currentPlan` may already be the new plan.
  const previousPlanRef = useRef<string | null>(null)
  const isReconciling =
    reconciliationAttempts > 0 && reconciliationAttempts < MAX_RECONCILIATION_ATTEMPTS
  const reconciliationFailed = reconciliationAttempts >= MAX_RECONCILIATION_ATTEMPTS
  const settlementSubscription = useGetPetSubscription(pet.id, { enabled: isReconciling })
  const settledPlan = settlementSubscription.data?.subscription?.plan
  const settlementStatus = settlementSubscription.data?.subscription?.status

  const reportReconciliationTimeout = useEffectEvent(() => {
    Sentry.captureException(
      new Error('Subscription upgrade not reflected after card confirmation'),
      {
        extra: { petId: pet.id, plan: selectedPlan },
      }
    )
  })

  const reportUpgraded = useEffectEvent(() => {
    captureEvent('subscription_upgraded', {
      patient_id: pet.id,
      from_plan: previousPlanRef.current,
      to_plan: selectedPlan,
    })
  })

  useEffect(() => {
    if (success) reportUpgraded()
  }, [success])

  useEffect(() => {
    if (!selectedPlan || reconciliationAttempts === 0) return

    if (
      settledPlan === selectedPlan &&
      settlementStatus &&
      HEALTHY_SUBSCRIPTION_STATUSES.has(settlementStatus)
    ) {
      queryClient.invalidateQueries({ queryKey: invoicesQueryKey() })
      setSuccess(true)
      return
    }

    if (reconciliationAttempts >= MAX_RECONCILIATION_ATTEMPTS) {
      reportReconciliationTimeout()
      return
    }

    const timer = setTimeout(async () => {
      await settlementSubscription.refetch()
      setReconciliationAttempts((attempts) => attempts + 1)
    }, RECONCILIATION_POLL_MS)

    return () => clearTimeout(timer)
  }, [
    reconciliationAttempts,
    selectedPlan,
    settledPlan,
    settlementStatus,
    settlementSubscription.refetch,
    queryClient,
  ])

  async function invalidateAfterUpgrade() {
    await queryClient.invalidateQueries({ queryKey: petSubscriptionQueryKey(pet.id) })
    queryClient.invalidateQueries({ queryKey: invoicesQueryKey() })
  }

  function handlePaymentConfirmed() {
    if (!selectedPlan) return

    setIncompletePayment(null)
    setReconciliationAttempts(1)
    queryClient.invalidateQueries({ queryKey: invoicesQueryKey() })
  }

  async function confirmUpgrade() {
    if (!paymentMethodId || !selectedPlan || !preview.data) return

    previousPlanRef.current ??= preview.data.currentPlan

    try {
      const response = await upgrade.mutateAsync({
        plan: selectedPlan,
        paymentMethodId,
        attemptToken,
      })

      if (response.incompletePayment) {
        setIncompletePayment(response.incompletePayment)

        if (response.incompletePayment.clientSecret && stripe) {
          setConfirmingCard(true)
          const result = await stripe.confirmCardPayment(response.incompletePayment.clientSecret, {
            payment_method: paymentMethodId,
          })

          if (result.error) {
            setInitialCardError(result.error.message ?? 'Payment failed')
            return
          }

          handlePaymentConfirmed()
        }
        return
      }

      await invalidateAfterUpgrade()
      setSuccess(true)
    } catch {
      // mutateAsync re-throws; error state is available via upgrade.error.
    } finally {
      setConfirmingCard(false)
      setAttemptToken(crypto.randomUUID())
    }
  }

  const isProcessing = upgrade.isPending || confirmingCard || isReconciling
  const selectedOption = preview.data?.options.find((o) => o.plan === selectedPlan) ?? null

  if (preview.isError && preview.error?.name !== 'ConflictError') {
    return (
      <NotFound
        headline={['Something went', 'wrong']}
        message="We can't upgrade this pet's subscription right now."
        documentTitle="Upgrade unavailable"
        accessory={
          <Alert variant="warning" className="w-full max-w-md text-left">
            <MessageCircle />
            <AlertTitle>Need help?</AlertTitle>
            <AlertDescription>Message our team and we&apos;ll get it sorted.</AlertDescription>
            <AlertAction>
              <ContactSupportAction />
            </AlertAction>
          </Alert>
        }
        backLink={{ label: 'Back to your pets', to: `${routes.account.pets.index}?pet=${pet.id}` }}
      />
    )
  }

  if (success) {
    return (
      <PageContainer
        backLink={{ to: `${routes.account.pets.index}?pet=${pet.id}`, label: 'Back to your pets' }}
        headline={[
          { text: 'Plan', color: 'green' },
          { text: 'upgraded!', color: 'orange' },
        ]}
      >
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <div className="flex size-16 items-center justify-center rounded-full bg-green">
            <Check className="size-8" />
          </div>
          <p className="text-body-lg">
            {pet.name}&apos;s plan has been upgraded
            {selectedPlan ? ` to ${SUBSCRIPTION_PLAN_LABELS[selectedPlan]}` : ''}.
          </p>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer
      backLink={{ to: `${routes.account.pets.index}?pet=${pet.id}`, label: 'Back to your pets' }}
      headline={[
        { text: 'Upgrade', color: 'green' },
        { text: `${pet.name}'s plan`, color: 'pink' },
      ]}
    >
      {reconciliationFailed ? (
        <PaymentReconciliationFailed message={`We had some trouble upgrading ${pet.name}'s plan`} />
      ) : isReconciling ? (
        <PaymentProcessing />
      ) : incompletePayment ? (
        <StripePaymentWidget
          clientSecret={incompletePayment.clientSecret}
          hostedInvoiceUrl={incompletePayment.hostedInvoiceUrl}
          label="Complete payment"
          region={member.region}
          initialError={initialCardError ?? undefined}
          onSuccess={handlePaymentConfirmed}
        />
      ) : preview.isPending || plans.isPending ? (
        <LoadingSpinner />
      ) : preview.isError || upgrade.error?.name === 'ConflictError' ? (
        <Alert variant="info">
          <MessageCircle />
          <AlertTitle>You can&apos;t upgrade this subscription via the member portal</AlertTitle>
          <AlertDescription>
            Please reach out to the care team and we&apos;ll get your upgrade sorted.
          </AlertDescription>
          <AlertAction>
            <ContactSupportAction />
          </AlertAction>
        </Alert>
      ) : preview.data ? (
        <div className="flex flex-col gap-6">
          <p className="text-body-md text-muted-foreground">
            {pet.name} is currently on the{' '}
            <strong>{SUBSCRIPTION_PLAN_LABELS[preview.data.currentPlan]}</strong> plan. Choose an
            upgrade below.
          </p>

          <fieldset
            className="flex flex-col gap-4 md:flex-row md:items-stretch"
            aria-label="Upgrade options"
          >
            {preview.data.options.map((option) => (
              <UpgradeOptionCard
                key={option.plan}
                option={option}
                region={member.region}
                plans={plans.data?.plans ?? []}
                selected={selectedPlan === option.plan}
                onSelect={() => setSelectedPlan(option.plan)}
              />
            ))}
          </fieldset>

          {selectedOption ? (
            <div className="flex flex-col gap-3 rounded-lg border-2 border-foreground bg-baby-blue p-6">
              <p className="text-body-md font-bold">
                You&apos;ll be charged{' '}
                {formatCurrency(selectedOption.amount, selectedOption.currency)} today.
              </p>
              <p>
                Your {SUBSCRIPTION_PLAN_LABELS[selectedOption.plan]} plan will start today and run
                to{' '}
                {new Date(selectedOption.newPeriodEnd).toLocaleDateString(region.locale, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
                .
              </p>
              {selectedOption.lineItems.length > 0 ? (
                <Collapsible className="mt-1">
                  <CollapsibleTrigger className="inline-flex items-center gap-1 text-sm font-semibold underline underline-offset-4 hover:no-underline cursor-pointer">
                    View breakdown
                    <ChevronDown className="size-4 transition-transform [[data-panel-open]_&]:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ul className="mt-3 flex flex-col gap-1 text-sm">
                      {selectedOption.lineItems.map((item) => (
                        <li
                          key={`${item.description ?? 'line-item'}-${item.amount}-${item.quantity}`}
                          className="flex justify-between"
                        >
                          <span>{item.description}</span>
                          <span>{formatCurrency(item.amount, selectedOption.currency)}</span>
                        </li>
                      ))}
                      <hr className="border-foreground/30" />
                      <li className="flex justify-between font-bold">
                        <span>Total</span>
                        <span>
                          {formatCurrency(selectedOption.amount, selectedOption.currency)}
                        </span>
                      </li>
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-col gap-2">
            <h2 className="text-sm font-medium text-muted-foreground">Payment method</h2>
            <PaymentMethods value={paymentMethodId} onChange={setPaymentMethodId} />
          </div>

          <Button
            type="button"
            onClick={confirmUpgrade}
            disabled={isProcessing || !selectedPlan || !paymentMethodId}
            className="h-14 w-full"
          >
            <Lock className="size-5" />
            {isProcessing ? 'Upgrading...' : 'Confirm upgrade'}
          </Button>

          {upgrade.error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>
                {upgrade.error.name === 'InternalError' || !upgrade.error.message
                  ? "Something's gone wrong on our end"
                  : 'Something went wrong'}
              </AlertTitle>
              <AlertDescription>
                {upgrade.error.name === 'InternalError' || !upgrade.error.message
                  ? 'The team has been notified. Please try again in a few moments.'
                  : upgrade.error.message}
              </AlertDescription>
            </Alert>
          ) : null}
        </div>
      ) : null}
    </PageContainer>
  )
}

function UpgradeOptionCard({
  option,
  region,
  plans,
  selected,
  onSelect,
}: {
  option: UpgradeOption
  region: RegionId
  plans: readonly PortalPlan[]
  selected: boolean
  onSelect: () => void
}) {
  const pricing = describePlanAmount(plans, option.plan, region, option.planAmount)
  const listAmount = plans.find((p) => p.plan === option.plan)?.amount
  const discounted = listAmount !== undefined && option.planAmount < listAmount

  return (
    <PlanOptionCard
      name="upgrade-plan"
      value={option.plan}
      title={SUBSCRIPTION_PLAN_LABELS[option.plan]}
      pricing={pricing}
      fullPrice={discounted ? describePlanPrice(plans, option.plan, region)?.price : undefined}
      discountNote={
        option.discounts.length
          ? `Includes your ${option.discounts.map((d) => d.name).join(' and ')}`
          : undefined
      }
      checked={selected}
      onSelect={onSelect}
      selectedClassName="bg-yellow"
    >
      <p className="mt-3 font-bold">{formatCurrency(option.amount, option.currency)} due today</p>
    </PlanOptionCard>
  )
}
